import { Platform } from 'react-native';

let webrtcPolyfillsRegistered = false;
export function registerWebrtcPolyfills() {
  if (webrtcPolyfillsRegistered) {
    return;
  }
  if (Platform.OS === 'web' && typeof window === 'undefined') {
    return;
  }
  webrtcPolyfillsRegistered = true;
  if (Platform.OS !== 'web') {
    require('react-native-webrtc').registerGlobals();
    return;
  }
  globalThis.RTCPeerConnection = globalThis.RTCPeerConnection || globalThis.mozRTCPeerConnection || globalThis.webkitRTCPeerConnection;
  globalThis.RTCSessionDescription = globalThis.RTCSessionDescription || globalThis.mozRTCSessionDescription || globalThis.webkitRTCSessionDescription;
  globalThis.RTCIceCandidate = globalThis.RTCIceCandidate || globalThis.mozRTCIceCandidate || globalThis.webkitRTCIceCandidate;

  // @ts-ignore
  window.MediaStreamTrack.prototype._switchCamera = function _switchCamera() {
    console.warn('_switchCamera is not implemented on web.');
  };

  // @ts-ignore
  window.mediaDevices = navigator.mediaDevices;
  // @ts-ignore
  window.permissions = navigator.permissions;
}

export function registerRequestIdleCompat() {
  if (typeof window === 'undefined' || window.requestIdleCallback) {
    return;
  }
  console.log('registering request idle compat');

  (window as any).requestIdleCallback = function (cb: any) {
    const start = Date.now();
    return setTimeout(() => {
      cb({
        didTimeout: false,
        // Simulates standard 50ms remaining frame budget
        timeRemaining: () => Math.max(0, 50 - (Date.now() - start)),
      });
    }, 1);
  };

  // Always match it with a working cancel function
  (window as any).cancelIdleCallback = function (id: number) {
    clearTimeout(id);
  };
}

export function registerCacheApiCompat() {
  // Guard clause: If the browser natively supports the Cache API, stand down
  if ('caches' in window) return;
  console.log('registering localforage cache api compat');
  const localforage = require('localforage');
  // Create an isolated IndexedDB store dedicated entirely to the Cache API fallback
  const cacheStore = localforage.createInstance({
    name: 'MatapCacheFallback',
    storeName: 'cache_data',
    driver: localforage.INDEXEDDB, // Force IndexedDB utilization
  });

  (window as any).caches = {
    /**
     * Global caches.delete(cacheName)
     * Drops all database entries tied to a specific cache namespace
     */
    delete: async (cacheName: string) => {
      try {
        const keys = await cacheStore.keys();
        const prefix = `${cacheName}:`;
        let deletedAny = false;

        for (const key of keys) {
          if (key.startsWith(prefix)) {
            await cacheStore.removeItem(key);
            deletedAny = true;
          }
        }
        return deletedAny;
      } catch (err) {
        console.error('Failed to clear cache bucket from localforage:', err);
        return false;
      }
    },

    /**
     * Global caches.open(cacheName)
     */
    open: async (cacheName: string) => {
      return {
        /**
         * Mimics cache.match()
         */
        match: async (request: any) => {
          const url = typeof request === 'string' ? request : request.url;
          const storageKey = `${cacheName}:${url}`;

          try {
            // localforage automatically deserializes stored JS objects natively
            const cachedData: any = await cacheStore.getItem(storageKey);
            if (!cachedData) return undefined;

            return new Response(cachedData.body, {
              status: cachedData.status,
              headers: new Headers(cachedData.headers),
            });
          } catch (e) {
            console.error('Failed to read backfilled localforage item', e);
            return undefined;
          }
        },

        /**
         * Mimics cache.put()
         */
        put: async (request: any, response: any) => {
          const url = typeof request === 'string' ? request : request.url;
          const storageKey = `${cacheName}:${url}`;

          const responseClone = response.clone();

          // Read as a Blob to safely preserve binary and text data alike
          const bodyBlob = await responseClone.blob();

          const headers: Record<string, string> = {};
          responseClone.headers.forEach((value: string, key: string) => {
            headers[key] = value;
          });

          // Store natively as an object without strings/manual serialization
          const cachePayload = {
            body: bodyBlob,
            status: responseClone.status,
            headers: headers,
          };

          try {
            await cacheStore.setItem(storageKey, cachePayload);
          } catch (e) {
            console.error('Failed to write item to localforage IndexedDB:', e);
          }
        },

        /**
         * Mimics cache.delete()
         */
        delete: async (request: any) => {
          const url = typeof request === 'string' ? request : request.url;
          const storageKey = `${cacheName}:${url}`;

          try {
            const item = await cacheStore.getItem(storageKey);
            if (item) {
              await cacheStore.removeItem(storageKey);
              return true;
            }
            return false;
          } catch (e) {
            console.error('Failed to execute item delete inside localforage:', e);
            return false;
          }
        },
      };
    },
  };
}

export function registerFindLastIndexCompat() {
  if (!Array.prototype.findLastIndex) {
    Object.defineProperty(Array.prototype, 'findLastIndex', {
      value: function (callback: any, thisArg: any) {
        for (let i = this.length - 1; i >= 0; i--) {
          if (callback.call(thisArg, this[i], i, this)) {
            return i;
          }
        }
        return -1;
      },
      writable: true,
      configurable: true,
    });
  }
}
